/**
 * Color By Pin Context
 * Manages whether the Color By legend is pinned to the legend container
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from 'react';

interface ColorByPinContextValue {
  isPinned: boolean;
  togglePin: () => void;
  setIsPinned: (pinned: boolean) => void;
}

const ColorByPinContext = createContext<ColorByPinContextValue | null>(null);

interface ColorByPinProviderProps {
  children: React.ReactNode;
}

export const ColorByPinProvider: React.FC<ColorByPinProviderProps> = ({
  children,
}) => {
  const [isPinned, setIsPinned] = useState(false);

  const togglePin = useCallback(() => {
    setIsPinned((prev) => !prev);
  }, []);

  const value = useMemo(
    () => ({
      isPinned,
      togglePin,
      setIsPinned,
    }),
    [isPinned, togglePin],
  );

  return (
    <ColorByPinContext.Provider value={value}>
      {children}
    </ColorByPinContext.Provider>
  );
};

export const useColorByPin = () => {
  const context = useContext(ColorByPinContext);
  if (!context) {
    throw new Error('useColorByPin must be used within a ColorByPinProvider');
  }
  return context;
};
